import { useEffect, useMemo } from "react";
import { useFieldArray, useWatch } from "react-hook-form";
import {
  Stack,
  Title,
  Divider,
  Group,
  Text,
  Badge,
  ActionIcon,
  Button,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { TextInput, Select } from "react-hook-form-mantine";
import CustomFieldsRenderer from "./CustomFieldsRenderer";
import { useTranslation } from "react-i18next";

export default function TicketsForm({
  control,
  ticketTypes,
  ticketCustomFields = [],
  maxQuantity,
  disableQuantityChange = false,
}) {
  const { t } = useTranslation();
  const { fields, replace, append, remove } = useFieldArray({
    control,
    name: "tickets",
  });

  const registrationType = useWatch({ control, name: "type" });
  const billingFirstName = useWatch({ control, name: "firstName" });
  const billingLastName = useWatch({ control, name: "lastName" });
  const billingEmail = useWatch({ control, name: "email" });

  const hasTicketTypes = ticketTypes && ticketTypes.length > 0;
  const canRemoveTicket = !disableQuantityChange && fields.length > 1;
  const canAddTicket =
    !disableQuantityChange && !(fields.length >= maxQuantity);

  const initAdditionalFields = (fields, existing = {}) => {
    const obj = { ...existing };
    fields.forEach((f) => {
      if (!(f.key in obj)) obj[f.key] = undefined;
    });
    return obj;
  };

  const createTicket = (usesBillingInfo = false) => {
    const canFillFromBilling =
      usesBillingInfo && registrationType === "individual";
    return {
      firstName: canFillFromBilling ? billingFirstName : "",
      lastName: canFillFromBilling ? billingLastName : "",
      email: canFillFromBilling ? billingEmail : "",
      ticketTypeId: null,
      additionalFields: initAdditionalFields(ticketCustomFields),
    };
  };

  useEffect(() => {
    if (fields.length === 0) {
      replace([createTicket(true)]);
    }
  }, []);

  const ticketTypeOptions = useMemo(() => {
    if (!hasTicketTypes) return [];
    return ticketTypes.map((t) => ({
      value: String(t.id),
      label: t.price ? `${t.name} (${t.price})` : t.name,
    }));
  }, [ticketTypes, hasTicketTypes]);

  const renderSelectOption = ({ option }) => {
    const ticket = ticketTypes.find((t) => String(t.id) === option.value);
    if (!ticket) return option.label;

    return (
      <Group justify="space-between" style={{ width: "100%" }}>
        <Group style={{ flex: 1 }}>
          <Text fw={700}>{ticket.name}</Text>
          {ticket.description && (
            <Text size="xs" c="dimmed">
              {ticket.description}
            </Text>
          )}
        </Group>
        {ticket.price && (
          <Group style={{ marginLeft: 8 }}>
            <Badge>{ticket.price}</Badge>
          </Group>
        )}
      </Group>
    );
  };

  return (
    <Stack gap="md">
      <Title order={4}>{t("tickets")}</Title>
      <Divider />

      {fields.map((field, index) => (
        <Stack
          key={field.id}
          gap="sm"
          style={{
            padding: "12px",
            border: "1px solid #e9ecef",
            borderRadius: "8px",
            backgroundColor: "#f8f9fa",
          }}
        >
          <Group justify="space-between">
            <Title order={5}>
              {t("attendee")} #{index + 1}
            </Title>
            {canRemoveTicket && (
              <ActionIcon
                color="red"
                variant="light"
                aria-label={t("remove_ticket", "Remove ticket")}
                onClick={() => remove(index)}
              >
                <IconTrash size={18} />
              </ActionIcon>
            )}
          </Group>

          <TextInput
            control={control}
            name={`tickets.${index}.firstName`}
            label={t("first_name")}
            placeholder={t("enter_first_name")}
            withAsterisk
          />

          <TextInput
            control={control}
            name={`tickets.${index}.lastName`}
            label={t("last_name")}
            placeholder={t("enter_last_name")}
            withAsterisk
          />

          <TextInput
            control={control}
            name={`tickets.${index}.email`}
            label={t("email")}
            type="email"
            placeholder={t("enter_email")}
            withAsterisk
          />

          {hasTicketTypes && (
            <Select
              control={control}
              name={`tickets.${index}.ticketTypeId`}
              label={t("ticket_type")}
              placeholder={t("select_option")}
              withAsterisk
              data={ticketTypeOptions}
              allowDeselect={false}
              renderOption={renderSelectOption}
            />
          )}

          {ticketCustomFields.length > 0 && (
            <CustomFieldsRenderer
              control={control}
              customFields={ticketCustomFields}
              parentPath={`tickets.${index}.additionalFields`}
            />
          )}
        </Stack>
      ))}

      {canAddTicket && (
        <Button
          variant="light"
          onClick={() => append(createTicket(false), { shouldFocus: false })}
        >
          {t("add_ticket", "Add a ticket")}
        </Button>
      )}
    </Stack>
  );
}
